import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ChevronLeft, FileArchive, Plus, Trash } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EQUIPMENT_VALUES } from "@/lib/constants";
import {
  EquipmentUpdateRequest,
  updateEquipmentFormSchema,
} from "@/api/update-equipment";
import {
  useUpdateEquipment,
  useEquipmentCertificate,
  useEquipmentDetails,
} from "@/hooks";
import { Separator } from "@/components/ui/separator";
import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
  TableHead,
} from "@/components/ui/table";

export default function EditEquipmentPage() {
  const { id } = useParams();

  const { data: equipment, isLoading } = useEquipmentDetails(id ?? "");
  const { mutateAsync: getEquipmentCertificate } = useEquipmentCertificate();
  const updateEquipment = useUpdateEquipment();

  const updateEquipmentForm = useForm<EquipmentUpdateRequest>({
    resolver: zodResolver(updateEquipmentFormSchema),
  });

  const [piecewiseIntervals, setPiecewiseIntervals] = useState<
    Array<{
      id?: string;
      start: number;
      end: number;
      offset: number;
      scalar: number;
      deleted: boolean;
    }>
  >([{ start: 0, end: 100, offset: 0, scalar: 1, deleted: false }]);

  useEffect(() => {
    if (equipment) {
      updateEquipmentForm.reset({
        id: equipment?.details.id,
        typeId: EQUIPMENT_VALUES.find(
          (value) => value.label === equipment?.details.equipmentType
        )?.value,
        serialNumber: equipment?.details.serialNumber,
        brand: equipment?.details.brand,
        model: equipment?.details.model,
        calibratedAt: equipment?.details.calibratedAt ?? undefined,
        nextCalibration: equipment?.details.nextCalibration ?? undefined,
        enableCalibrationCurve: equipment?.correctionIntervals.length > 0,
      });

      // Initialize piecewiseIntervals if they exist
      if (equipment.correctionIntervals.length > 0) {
        setPiecewiseIntervals(
          equipment.correctionIntervals.map((interval) => ({
            id: interval.id,
            start: Number(interval.start),
            end: Number(interval.end),
            offset: interval.offset,
            scalar: interval.scalar,
            deleted: false,
          }))
        );
      }
    }
  }, [equipment, updateEquipmentForm]);

  // for when submit is clicked
  const navigate = useNavigate();
  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="container mx-auto pb-8 grid gap-3">
          <div className="flex items-center gap-0">
            <Link
              to="/equipos"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <ChevronLeft className="size-4" />
              Regresar
            </Link>
          </div>
          <div>
            <h1 className="text-lg font-semibold">Editar Equipo</h1>
            <p className="text-xs text-muted-foreground">
              Actualice los detalles del equipo seleccionado
            </p>
          </div>
          <Separator />
          <Form {...updateEquipmentForm}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={updateEquipmentForm.control}
                name="id"
                render={({ field }) => (
                  <FormItem className="hidden">
                    <FormLabel>ID</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateEquipmentForm.control}
                name="typeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Equipo</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Seleccione el tipo de equipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {EQUIPMENT_VALUES.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateEquipmentForm.control}
                name="serialNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de Serie</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ingrese el número de serie"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateEquipmentForm.control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Marca</FormLabel>
                    <FormControl>
                      <Input placeholder="Ingrese la marca" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateEquipmentForm.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modelo</FormLabel>
                    <FormControl>
                      <Input placeholder="Ingrese el modelo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateEquipmentForm.control}
                name="calibratedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha de Calibración</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateEquipmentForm.control}
                name="nextCalibration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Próxima Calibración</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={updateEquipmentForm.control}
              name="certificate"
              render={({ field }) => {
                const deleted = updateEquipmentForm.watch("deleteCertificate");
                if (deleted) {
                  field.value = undefined;
                  return <></>;
                }

                return (
                  <FormItem>
                    <FormLabel>Archivo de Certificado</FormLabel>
                    {equipment?.details.certificatePath && !field.value && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={async () => {
                          const url = await getEquipmentCertificate({
                            equipmentId: equipment?.details.id ?? "",
                          });
                          window.open(url, "_blank");
                        }}
                        className="gap-2"
                      >
                        <FileArchive className="size-4" />
                        Descargar certificado
                      </Button>
                    )}
                    <FormControl>
                      <Input
                        accept=".pdf"
                        type="file"
                        onChange={(event) =>
                          field.onChange(
                            event.target.files && event.target.files[0]
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {equipment?.details.certificatePath && (
              <FormField
                control={updateEquipmentForm.control}
                name="deleteCertificate"
                render={({ field }) => {
                  return (
                    <FormItem className="flex items-center gap-2 mt-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="cursor-pointer">
                        Eliminar Certificado
                      </FormLabel>
                    </FormItem>
                  );
                }}
              />
            )}

            {updateEquipmentForm.watch("typeId") === "LlcbnmHu" && (
              <div className="flex items-center gap-2 mt-4">
                <Checkbox
                  id="enableCalibrationCurve"
                  checked={
                    updateEquipmentForm.watch("enableCalibrationCurve") || false
                  }
                  onCheckedChange={(checked) => {
                    updateEquipmentForm.setValue(
                      "enableCalibrationCurve",
                      checked === true
                    );
                  }}
                />
                <Label htmlFor="enableCalibrationCurve">
                  Incluir Correcciónes
                </Label>
              </div>
            )}

            {updateEquipmentForm.watch("enableCalibrationCurve") &&
              updateEquipmentForm.watch("typeId") === "LlcbnmHu" && (
                <>
                  <Button
                    type="button"
                    variant="success"
                    size="sm"
                    className="gap-2 w-min justify-self-end mt-4"
                    onClick={() => {
                      const lastInterval = piecewiseIntervals
                        .slice()
                        .reverse()
                        .find((interval) => !interval.deleted);

                      setPiecewiseIntervals([
                        ...piecewiseIntervals,
                        {
                          start: lastInterval?.end ?? 0,
                          end: (lastInterval?.end ?? 0) + 100,
                          offset: 0,
                          scalar: 1,
                          deleted: false,
                        },
                      ]);
                    }}
                  >
                    <Plus className="size-4" />
                    Agregar intervalo
                  </Button>
                  <Table>
                    <TableHeader>
                      <TableRow className="text-sm tracking-tight">
                        <TableHead>
                          <p>Minimo</p>
                          <p>(lux)</p>
                        </TableHead>
                        <TableHead>
                          <p>Maximo</p>
                          <p>(lux)</p>
                        </TableHead>
                        <TableHead>
                          <p>Corrección </p>
                          <p>(Aditiva)</p>
                        </TableHead>
                        <TableHead>
                          <p>Corrección </p>
                          <p>(Multiplicativa)</p>
                        </TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    {piecewiseIntervals.map(
                      (interval, index) =>
                        !interval.deleted && (
                          <TableRow key={index} className="p-0 m-0 font-mono">
                            <TableCell className="p-0 m-0">
                              <Input
                                id={`interval-${index}-start`}
                                type="number"
                                value={interval.start}
                                className="w-full h-full border-none shadow-none"
                                onChange={(e) => {
                                  const newIntervals = [...piecewiseIntervals];
                                  newIntervals[index].start = Number(
                                    e.target.value
                                  );
                                  setPiecewiseIntervals(newIntervals);
                                }}
                              />
                            </TableCell>
                            <TableCell className="p-0 m-0">
                              <Input
                                id={`interval-${index}-end`}
                                type="number"
                                value={interval.end}
                                onChange={(e) => {
                                  const newIntervals = [...piecewiseIntervals];
                                  newIntervals[index].end = Number(
                                    e.target.value
                                  );
                                  setPiecewiseIntervals(newIntervals);
                                }}
                                className="w-full h-full border-none shadow-none"
                              />
                            </TableCell>
                            <TableCell className="p-0 m-0">
                              <Input
                                id={`interval-${index}-offset`}
                                type="number"
                                value={interval.offset}
                                onChange={(e) => {
                                  const newIntervals = [...piecewiseIntervals];
                                  newIntervals[index].offset = Number(
                                    e.target.value
                                  );
                                  setPiecewiseIntervals(newIntervals);
                                }}
                                className="w-full h-full border-none shadow-none"
                              />
                            </TableCell>
                            <TableCell className="p-0 m-0">
                              <Input
                                id={`interval-${index}-scalar`}
                                type="number"
                                value={interval.scalar}
                                onChange={(e) => {
                                  const newIntervals = [...piecewiseIntervals];
                                  newIntervals[index].scalar = Number(
                                    e.target.value
                                  );
                                  setPiecewiseIntervals(newIntervals);
                                }}
                                className="w-full h-full border-none shadow-none"
                              />
                            </TableCell>
                            <TableCell className="p-0 m-0">
                              <Button
                                type="button"
                                disabled={piecewiseIntervals.length < 2}
                                variant="ghost"
                                className="hover:bg-transparent focus:bg-transparent hover:cursor-pointer"
                                size="icon"
                                onClick={() => {
                                  const newIntervals = [...piecewiseIntervals];
                                  if (newIntervals[index].id) {
                                    newIntervals[index].deleted = true;
                                  } else {
                                    newIntervals.splice(index, 1);
                                  }
                                  setPiecewiseIntervals(newIntervals);
                                }}
                              >
                                <Trash className="size-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )
                    )}
                  </Table>
                </>
              )}

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="destructive" type="button" asChild>
                <Link to="/equipos">Cancelar</Link>
              </Button>
              <Button
                type="submit"
                disabled={updateEquipment.isPending}
                onClick={updateEquipmentForm.handleSubmit(async (data) => {
                  // Add calibration intervals to the data if enabled
                  if (data.enableCalibrationCurve) {
                    data.calibrationIntervals = piecewiseIntervals;
                  } else {
                    // if uncheck correction curve while lighting, we delete all intervals
                    data.calibrationIntervals =
                      equipment?.correctionIntervals.map((interval) => ({
                        id: interval.id,
                        deleted: true,
                      }));
                  }

                  await updateEquipment.mutateAsync(data);
                  navigate("/equipos");
                })}
                className="gap-2"
              >
                {updateEquipment.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Actualizando...
                  </>
                ) : (
                  "Actualizar equipo"
                )}
              </Button>
            </div>
          </Form>
        </div>
      )}
    </>
  );
}
